import { bannerImage, firstMediaUrl, normalizeCourse } from '../utils/media'

const API_BASE = (import.meta.env.VITE_API_BASE || 'https://365wm.longmeika.com/api').replace(/\/$/, '')
const PUBLIC_COURSES = import.meta.env.VITE_PUBLIC_COURSES_URL || 'https://365wm.longmeika.com/web/assets/public-courses.json'
const SMS_SEND_PATH = import.meta.env.VITE_SMS_SEND_PATH || ''
const SMS_LOGIN_PATH = import.meta.env.VITE_SMS_LOGIN_PATH || ''
const SESSION_TTL = 7 * 24 * 60 * 60 * 1000
const COURSE_CACHE_TTL = 5 * 60 * 1000
const ENTITLEMENT_CACHE_TTL = 60 * 1000
const AUTH_CHANGED_EVENT = 'trade365-auth-changed'
const courseCache = new Map()
let entitlementCache = null

function token() {
  const value = localStorage.getItem('trade365_web_token') || ''
  const loginTime = Number(localStorage.getItem('trade365_web_login_time') || 0)
  if (value && (!loginTime || Date.now() - loginTime > SESSION_TTL)) {
    logout()
    return ''
  }
  return value
}

async function request(path, options = {}) {
  const headers = new Headers(options.headers || {})
  headers.set('Accept', 'application/json')
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (token()) headers.set('Authorization', `Bearer ${token()}`)
  const response = await fetch(`${API_BASE}${path}`, { ...options, headers })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || [401, 403, 500].includes(Number(payload.code))) {
    throw new Error(payload.msg || `请求失败 (${response.status})`)
  }
  return payload.data ?? payload
}

function requireConfiguredPath(path, label) {
  if (!path) throw new Error(`${label}尚未在后台启用`)
  return path
}

export function rows(payload) {
  if (Array.isArray(payload)) return payload
  return payload?.rows || payload?.resList || payload?.list || payload?.records || payload?.data?.rows || payload?.data || []
}

function resetSessionCaches() {
  courseCache.clear()
  entitlementCache = null
}

function courseIds(item = {}) {
  return [
    item.curriculumId,
    item.courseId,
    item.curriculum?.id,
    item.course?.id,
    item.id
  ].filter(value => value !== undefined && value !== null && String(value) !== '').map(String)
}

async function getEntitlements() {
  const accessToken = token()
  if (!accessToken) return { ownedCourseIds: new Set(), isVip: false }
  if (entitlementCache && entitlementCache.accessToken === accessToken && Date.now() - entitlementCache.time < ENTITLEMENT_CACHE_TTL) {
    return entitlementCache.value
  }

  const pending = Promise.all([
    getMyCourses({ pageNum: 1, pageSize: 500 }).catch(() => null),
    getVipInfo().catch(() => null)
  ]).then(([orders, vip]) => {
    const ownedCourseIds = new Set()
    rows(orders).forEach(item => courseIds(item).forEach(id => ownedCourseIds.add(id)))
    return {
      ownedCourseIds,
      isVip: Boolean(vip && Number(vip.vipFlag) === 0 && Number(vip.expireFlag) === 0)
    }
  })
  entitlementCache = { accessToken, time: Date.now(), value: pending }
  const value = await pending
  entitlementCache = { accessToken, time: Date.now(), value }
  return value
}

async function applyCourseEntitlements(courses) {
  const entitlements = await getEntitlements()
  return courses.map(course => {
    const hasBuyFlag = course.buyFlag !== undefined && course.buyFlag !== null
    const buyFlagAllowsAccess = hasBuyFlag && Number(course.buyFlag) === 0
    const hasFreeType = course.freeType !== undefined && course.freeType !== null
    const freeType = hasFreeType ? Number(course.freeType) : null
    const detailOwned = buyFlagAllowsAccess && (freeType === 2 || (freeType === 1 && !entitlements.isVip))
    const backendOwned = Number(course.orderFlag) === 1 || course.purchased === true || detailOwned
    const orderOwned = courseIds(course).some(id => entitlements.ownedCourseIds.has(id))
    const purchased = backendOwned || orderOwned
    const vipAccess = !purchased && freeType === 1 && (entitlements.isVip || buyFlagAllowsAccess)
    const accessGranted = purchased || freeType === 0 || vipAccess || buyFlagAllowsAccess
    return {
      ...course,
      orderFlag: purchased ? 1 : Number(course.orderFlag || 0),
      purchased,
      accessGranted,
      vipAccess
    }
  })
}

export async function getHomeData() {
  const [courses, categories, banners] = await Promise.all([
    request('/system/curriculum/list_app?pageNum=1&pageSize=24'),
    request('/system/cate/list_app?pageNum=1&pageSize=30'),
    request('/system/banner/list_app?pageNum=1&pageSize=10')
  ])
  const normalizedCourses = rows(courses).map(normalizeCourse)
  return {
    courses: await applyCourseEntitlements(normalizedCourses),
    categories: rows(categories),
    banners: rows(banners).map(item => ({ ...item, image: bannerImage(item) }))
  }
}

export async function getTeacher(id) {
  const teacher = await request(`/system/teacher/getInfo_app/${id}`)
  return {
    ...teacher,
    avatar: firstMediaUrl(teacher.avatar),
    banner: firstMediaUrl(teacher.banner)
  }
}

export async function getFeaturedTeachers(courseIds = [31, 30, 25, 22, 21]) {
  const courseResults = await Promise.all(courseIds.map((id) => getCourse(id).catch(() => null)))
  const candidates = courseResults.filter(Boolean).reduce((list, course) => {
    if (!course.teacherId || list.some((item) => String(item.teacherId) === String(course.teacherId))) return list
    list.push({
      teacherId: course.teacherId,
      courseId: course.id,
      name: course.teacherName,
      image: course.teacherAvatar,
      specialty: course.cateName || '外贸实战'
    })
    return list
  }, [])

  const teachers = await Promise.all(candidates.map(async (candidate) => {
    try {
      const teacher = await getTeacher(candidate.teacherId)
      if (Number(teacher.starFlag) !== 1 || Number(teacher.status) !== 0) return null
      const firstLine = String(teacher.description || '').split(/\r?\n/).map((line) => line.trim()).find(Boolean)
      return {
        ...candidate,
        id: teacher.id,
        name: teacher.name || candidate.name,
        image: firstMediaUrl(teacher.avatar || candidate.image),
        banner: firstMediaUrl(teacher.banner),
        specialty: firstLine && !/^\d+$/.test(firstLine) ? firstLine : candidate.specialty
      }
    } catch {
      return null
    }
  }))
  return teachers.filter(Boolean)
}

export async function getPublicCourses() {
  const response = await fetch(PUBLIC_COURSES)
  if (!response.ok) throw new Error('课程资料暂时无法读取')
  const payload = await response.json()
  return (payload.courses || []).map(normalizeCourse)
}

export async function getCourses(params = {}) {
  const query = new URLSearchParams({ pageNum: 1, pageSize: 60, ...params })
  const payload = await request(`/system/curriculum/list_app?${query}`)
  return applyCourseEntitlements(rows(payload).map(normalizeCourse))
}

export async function getCourseCategories(params = {}) {
  const query = new URLSearchParams({ pageNum: 1, pageSize: 50, ...params })
  const payload = await request(`/system/cate/list_app?${query}`)
  return rows(payload)
    .map(item => ({
      ...item,
      id: item.id ?? item.cateId,
      title: item.title || item.name || item.cateName || ''
    }))
    .filter(item => item.id != null && item.title)
}

export async function getCourse(id) {
  const cacheKey = String(id)
  const cached = courseCache.get(cacheKey)
  if (cached && Date.now() - cached.time < COURSE_CACHE_TTL) return (await applyCourseEntitlements([cached.course]))[0]
  try {
    const course = normalizeCourse(await request(`/system/curriculum/getInfo_app/${id}`))
    courseCache.set(cacheKey, { time: Date.now(), course })
    return (await applyCourseEntitlements([course]))[0]
  } catch (error) {
    const courses = await getPublicCourses()
    const course = courses.find((item) => String(item.id) === String(id))
    if (!course) throw error
    courseCache.set(cacheKey, { time: Date.now(), course })
    return (await applyCourseEntitlements([course]))[0]
  }
}

export async function getQuestions(params = {}) {
  const query = new URLSearchParams({ pageNum: 1, pageSize: 20, ...params })
  return request(`/system/qa/list_app?${query}`)
}

export async function getQuestion(id) {
  return request(`/system/qa/${id}`)
}

export async function searchCustoms(params = {}) {
  const query = new URLSearchParams({ pageNum: 1, pageSize: 20, orderByColumn: 'colum1', isAsc: 'desc', ...params })
  return request(`/system/hgInfo/list?${query}`)
}

export async function getCustomsDetail(id) {
  return request(`/system/hgInfo/${id}`)
}

export function isSmsLoginConfigured() {
  return Boolean(SMS_SEND_PATH && SMS_LOGIN_PATH)
}

export async function sendSmsCode(phoneNumber) {
  return request(requireConfiguredPath(SMS_SEND_PATH, '短信验证码接口'), {
    method: 'POST',
    body: JSON.stringify({ phoneNumber })
  })
}

export async function loginWithSms(phoneNumber, smsCode) {
  const result = await request(requireConfiguredPath(SMS_LOGIN_PATH, '手机号验证码登录接口'), {
    method: 'POST',
    body: JSON.stringify({ phoneNumber, smsCode })
  })
  const accessToken = result.token || result.accessToken
  if (!accessToken) throw new Error('后台未返回登录凭证')
  localStorage.setItem('trade365_web_token', accessToken)
  localStorage.setItem('trade365_web_login_time', String(Date.now()))
  resetSessionCaches()
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
  return result
}

export async function getProfile() {
  return request('/getInfo')
}

export async function getMyCourses(params = {}) {
  const query = new URLSearchParams({ pageNum: 1, pageSize: 100, ...params })
  return request(`/system/order/list_app?${query}`)
}

export async function getVipInfo() {
  return request('/system/userVip/info_app')
}

export async function getMyCoupons(params = {}) {
  const query = new URLSearchParams({ pageNum: 1, pageSize: 100, ...params })
  return request(`/system/userCoupon/list_inMy?${query}`)
}

export function logout() {
  localStorage.removeItem('trade365_web_token')
  localStorage.removeItem('trade365_web_login_time')
  resetSessionCaches()
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
}

export function hasActiveSession() {
  return Boolean(token())
}

export { API_BASE, AUTH_CHANGED_EVENT }
